/**
 * 仕入の作成画面 /purchases/:id（#233 3a、docs/93 §15.4・§15.10 ① Figma 229:134）。見積依頼書の作成画面と同じ骨組み（頭の帯・明細のカード・欄の部品）。
 *   頭の帯（2段）：1段目＝「‹」・題「仕入」・{vendor}（御中を付けない）・請求日・請求書番号・状態チップ・自動保存／右に ↶ ↷・主なボタン（下書き＝確定）＋「⋯」
 *           2段目＝{project} ›・元 {order} PO-0001 ›・原本 請求書.pdf ›／右に 合計（税込）・詳細 ⌄（社内メモ）
 *   条件の帯：受領日・検査完了日・支払期日（{vendor}の支払条件から・受領から 60日超は朱＋チップ）・登録番号・控除割合
 *   明細のカード：道具の行（行を追加・見出しを追加・「n 行 {order} PO-0001 から」）・明細の表（EstimateTable の外枠＋PURCHASE_COLS）・小計／消費税（打てる）／合計
 *     数量・単価を打つと金額は 数量×単価（既定）。金額も打てる（相手の請求書のとおり）。消費税は明細から計算して入り、打つと再計算しない（「自動に戻す」）
 *   下の帯：スイッチ「原価計上」・{project}（付け先）・原価（税抜）・{order}との差
 *   保存は共通の仕組み（lib/saveQueue.tsx）：打つ欄は 0.6 秒後・選ぶ／切り替えはすぐ・送るのは1本ずつ・版つき（409 PURCHASE_STALE は最新を読んで、この画面で修正した欄を重ねて送り直す）
 *   確定の前は ready()（保存できていなければ進まない）。受領から 60日超なら確定の前に確かめる（止めない）
 *   確定した仕入は明細・日付・金額を直せない（「⋯」→ 下書きに戻す）。原価計上・付け先・メモは直せる
 *   スマホ（幅 767px 以下・スマホ版）：閲覧のみ（帯は縦に積む）
 */
import {
  CONFLICT_MSG,
  PURCHASE_LIMITS,
  PURCHASE_MSG,
  TAX_CATEGORY_LABEL,
  UndoStack,
  VENDOR_PAYMENT_MSG,
  creditRatioText,
  purchaseLineAmount,
  purchaseOver60,
  saveGateText,
  type GroupList,
  type PartnerList,
  type Purchase,
  type PurchaseLine,
  type PurchaseLineInput,
  type PurchaseSave,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { DocField, DocHead } from "../../components/DocHead.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { DOTS_H, HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { notify } from "../../components/Notice.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { ErrorText, Field, Help, Switch, useToast } from "../../components/ui.jsx";
import { useUses } from "../../core/uses.js";
import { t, useLabels } from "../../labels.js";
import { fq, mdSlash, yen } from "../../lib/num.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { useNarrow } from "../../lib/useNarrow.js";
import { EstimateTable, TaxSelect, type TblCtx } from "../estimates/EstimateTable.jsx";
import { longestMoney, purchaseColumnsFor } from "../estimates/columns.js";
import { useEstimateColumns } from "../estimates/colWidths.js";
import { PurchaseStateChip } from "./parts.jsx";
import { PurchasePaymentsSection } from "./VendorPayments.jsx";

const KEY = (id: string) => ["purchase", id] as const;
const newId = (): string => crypto.randomUUID();
const same = (a: PurchaseLine[], b: PurchaseLine[]) => JSON.stringify(a) === JSON.stringify(b);
const inputOf = (l: PurchaseLine): PurchaseLineInput => ({
  id: l.id,
  kind: l.kind,
  name: l.name,
  spec: l.kind === "detail" ? l.spec : "",
  quantity: l.kind === "detail" ? l.quantity : null,
  unit: l.kind === "detail" ? l.unit : "",
  unitPrice: l.kind === "detail" ? l.unitPrice : null,
  amount: l.kind === "detail" ? l.amount : 0,
  taxCategory: l.taxCategory,
  note: l.note,
  itemId: l.itemId,
  orderLineId: l.kind === "detail" ? l.orderLineId : null,
});
const blank = (kind: PurchaseLine["kind"] = "detail"): PurchaseLine => ({
  id: newId(), kind, name: "", spec: "", quantity: null, unit: "", unitPrice: null, amount: 0, taxCategory: "standard", note: "", itemId: null, orderLineId: null, orderLine: null,
});
type Head = Pick<Purchase, "purchasedOn" | "vendorInvoiceNo" | "receivedOn" | "inspectedOn" | "paymentDueOn" | "memo">;
const headOf = (p: Purchase): Head => ({ purchasedOn: p.purchasedOn, vendorInvoiceNo: p.vendorInvoiceNo, receivedOn: p.receivedOn, inspectedOn: p.inspectedOn, paymentDueOn: p.paymentDueOn, memo: p.memo });
/** 欄の並び（Enter・Tab で次の欄へ） */
const FIELDS = ["name", "spec", "qty", "unit", "price", "amount", "note"] as const;
type FieldKey = (typeof FIELDS)[number];
/** 税率（0.1）で切り捨て（DB の trunc と同じ） */
const taxOf = (base: number, rate: number) => {
  const r4 = Math.round(rate * 10000);
  return base < 0 ? -Math.floor((-base * r4) / 10000) : Math.floor((base * r4) / 10000);
};

export function PurchasePage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const L = useLabels();
  const can = useUses();
  const narrow = useNarrow();
  const sizer = useEstimateColumns("estimate");
  const { toast } = useToast();
  const q = useQuery({ queryKey: KEY(id), queryFn: () => api<Purchase>("GET", `/purchases/${id}`) });
  const p = q.data;
  const back = useBackNav(p?.group ? { to: `/groups/${p.group.id}?tab=purchases`, label: p.group.name } : { to: "/purchases", label: PURCHASE_MSG.list });
  usePageName(PURCHASE_MSG.title);

  const [head, setHead] = useState<Head | null>(null);
  const [lines, setLines] = useState<PurchaseLine[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const version = useRef("");
  /** 送る前の直し（欄ごと）。断られたら残して、次に送るときに送り直す（#177） */
  const pending = useRef<Omit<PurchaseSave, "version">>({});
  const linesRef = useRef<PurchaseLine[]>([]);
  const undo = useRef(new UndoStack<PurchaseLine[]>(same));
  const [, bump] = useState(0);
  const [open, setOpen] = useState(false);
  const [dlg, setDlg] = useState<null | "confirm60" | "void" | "delete">(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState<{ id: string; f: FieldKey } | null>(null);
  const [taxText, setTaxText] = useState<number | null>(null);

  // はじめて読んだとき・別の仕入に移ったとき・確定や取消のあと（固めた値）に入れ直す
  useEffect(() => {
    if (!p) return;
    version.current = p.updatedAt;
    if (loadedId !== p.id || !p.canEdit) {
      setHead(headOf(p));
      setLines(p.lines);
      linesRef.current = p.lines;
      setLoadedId(p.id);
      undo.current.clear();
    }
  }, [p?.id, p?.canEdit, p?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAround = () => {
    void qc.invalidateQueries({ queryKey: ["purchases"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    if (p?.group) void qc.invalidateQueries({ queryKey: ["group", p.group.id] });
  };
  const put = (v: Purchase) => {
    qc.setQueryData(KEY(v.id), v);
    version.current = v.updatedAt;
  };

  const sv = useSaveQueue({
    wait: 600,
    maxWait: Infinity,
    send: async () => {
      const body = pending.current;
      pending.current = {};
      if (!Object.keys(body).length) return;
      const sentLines = body.lines;
      try {
        let v: Purchase;
        try {
          v = await api<Purchase>("PUT", `/purchases/${id}`, { ...body, version: version.current });
        } catch (e) {
          if (!(e instanceof ApiError && e.code === "PURCHASE_STALE")) throw e;
          // ほかで変わった：最新を読み、この画面で修正した欄だけを重ねて送り直す（明細はこの画面の形で上書き）
          const latest = await api<Purchase>("GET", `/purchases/${id}`);
          version.current = latest.updatedAt;
          v = await api<Purchase>("PUT", `/purchases/${id}`, { ...body, version: latest.updatedAt });
          notify({ text: CONFLICT_MSG.autoMerged });
        }
        put(v);
        // 送っている間に打った明細が無ければ、サーバーの答え（{order}の行）で入れ替える
        if (sentLines && !pending.current.lines && JSON.stringify(sentLines) === JSON.stringify(linesRef.current.map(inputOf))) {
          setLines(v.lines);
          linesRef.current = v.lines;
        }
        refreshAround();
      } catch (e) {
        pending.current = { ...body, ...pending.current };
        throw e;
      }
    },
  });
  const patch = (x: Omit<PurchaseSave, "version">, now = false) => {
    pending.current = { ...pending.current, ...x };
    sv.mark(now);
  };
  /** 明細を変える（変える前を控える＝↶ で戻せる。key が同じあいだは1手にまとめる） */
  const setRows = (next: PurchaseLine[], key: string | null = null, now = false) => {
    undo.current.record(linesRef.current, key);
    linesRef.current = next;
    setLines(next);
    patch({ lines: next.map(inputOf) }, now);
    bump((x) => x + 1);
  };
  const undoDo = (redo: boolean) => {
    const to = redo ? undo.current.redo(linesRef.current) : undo.current.undo(linesRef.current);
    if (!to) return;
    linesRef.current = to;
    setLines(to);
    patch({ lines: to.map(inputOf) }, true);
    bump((x) => x + 1);
  };

  const vendors = useQuery({ queryKey: ["partners", "", "active", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor"), enabled: !!p?.canEdit && !narrow });
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all"), enabled: !!p && p.status !== "voided" && !narrow });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!p || !head || loadedId !== p.id) return <div className="rqe pce" aria-busy="true" data-testid="purchase-page" />;
  const draft = p.canEdit && !narrow;
  const live = p.status !== "voided" && !narrow;
  const setH = (x: Partial<Head>, now = false) => {
    setHead({ ...head, ...x });
    patch(
      {
        ...(x.purchasedOn !== undefined ? { purchasedOn: x.purchasedOn } : {}),
        ...(x.vendorInvoiceNo !== undefined ? { vendorInvoiceNo: x.vendorInvoiceNo } : {}),
        ...(x.receivedOn !== undefined ? { receivedOn: x.receivedOn || null } : {}),
        ...(x.inspectedOn !== undefined ? { inspectedOn: x.inspectedOn || null } : {}),
        ...(x.paymentDueOn !== undefined ? { paymentDueOn: x.paymentDueOn || null } : {}),
        ...(x.memo !== undefined ? { memo: x.memo } : {}),
      },
      now,
    );
  };

  // ---- 合計（小計は画面の明細から。消費税は打った額か、明細から計算した額＝税率が1つのときは画面で、2つ以上は保存の答え） ----
  const subtotal = lines.filter((l) => l.kind === "detail").reduce((a, l) => a + l.amount, 0);
  const cats = new Set(lines.filter((l) => l.kind === "detail").map((l) => l.taxCategory));
  const autoTax = p.taxRate != null && cats.size <= 1 ? taxOf(subtotal, p.taxRate) : (p.taxSuggested ?? p.taxAmount);
  const taxAuto = pending.current.taxAmount !== undefined ? pending.current.taxAmount === null : p.taxAuto;
  const taxValue = !draft ? p.taxAmount : taxAuto ? autoTax : (taxText ?? p.taxAmount);
  const total = draft ? subtotal + taxValue : p.total;
  const over60 = purchaseOver60(head.receivedOn, head.paymentDueOn);
  const orderDiff = p.order ? (draft ? subtotal : p.subtotal) - p.order.subtotal : null;

  const act = async (fn: () => Promise<Purchase>, done?: string) => {
    setBusy(true);
    try {
      const v = await fn();
      put(v);
      setHead(headOf(v));
      setLines(v.lines);
      linesRef.current = v.lines;
      undo.current.clear();
      setTaxText(null);
      refreshAround();
      if (done) toast(done);
      setDlg(null);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  /** 確定：書きかけを送り終えてから。受領から 60日超は確かめる（止めない） */
  const confirm = async (checked = false) => {
    const why = await sv.ready();
    if (why != null) return toast(saveGateText("confirm", why), undefined, "error");
    if (!checked && over60) return setDlg("confirm60");
    await act(() => api<Purchase>("POST", `/purchases/${p.id}/confirm`, { version: version.current }), PURCHASE_MSG.confirmed);
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api<void>("DELETE", `/purchases/${p.id}`);
      refreshAround();
      toast(PURCHASE_MSG.deleted);
      nav(p.group ? `/groups/${p.group.id}?tab=purchases` : "/purchases", { replace: true });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
      setBusy(false);
    }
  };
  const actions: HeadAction[] = narrow
    ? []
    : p.status === "draft"
      ? [
          { label: PURCHASE_MSG.confirm, rank: 1, testId: "purchase-confirm", disabled: busy, onSelect: () => void confirm() },
          { label: PURCHASE_MSG.deleteDraft, group: "danger", danger: true, testId: "purchase-delete", onSelect: () => setDlg("delete") },
        ]
      : p.status === "confirmed"
        ? [
            // 支払の記録がある仕入は 下書きに戻す・取消 ができない（#233 3c。押せない項目は薄く出して下に1行＝#173 の形）
            { label: PURCHASE_MSG.unconfirm, testId: "purchase-unconfirm", disabled: p.paid > 0, ...(p.paid > 0 ? { note: VENDOR_PAYMENT_MSG.purchaseHasPaymentNote } : {}), onSelect: () => void act(() => api<Purchase>("POST", `/purchases/${p.id}/unconfirm`, { version: version.current }), PURCHASE_MSG.unconfirmed) },
            { label: PURCHASE_MSG.void, group: "danger", danger: true, testId: "purchase-void", disabled: p.paid > 0, ...(p.paid > 0 ? { note: VENDOR_PAYMENT_MSG.purchaseHasPaymentNote } : {}), onSelect: () => setDlg("void") },
          ]
        : [];
  const saveText = sv.state.kind === "saving" ? PURCHASE_MSG.saving : sv.state.kind === "saved" ? PURCHASE_MSG.autoSaved.replace("{at}", sv.state.at) : sv.state.kind === "error" ? `⚠ ${sv.state.msg}` : "";

  // ---- 明細の表 ----
  const taxCol = can("reducedTax");
  const cols = purchaseColumnsFor(sizer.widths, taxCol);
  const ctx: TblCtx = { cols, showInternal: false, readOnly: !draft, alt: true, onCover: false, field: false, takeoff: false, taxCol, taxDateHint: false, money: { price: longestMoney(lines.map((l) => (l.unitPrice == null ? "" : yen(l.unitPrice)))), amount: longestMoney(lines.map((l) => yen(l.amount))) } };
  const focusField = (lineId: string, f: FieldKey) => {
    setFocus({ id: lineId, f });
    const el = () => (f === "unit" ? document.getElementById(`pu_${lineId}`) : document.querySelector<HTMLElement>(`[data-line="${lineId}"] [data-f="${f}"]`));
    const now = el();
    if (now) now.focus();
    else window.requestAnimationFrame(() => el()?.focus());
  };
  const nextField = (lineId: string, f: FieldKey) => {
    const i = lines.findIndex((l) => l.id === lineId);
    const l = lines[i]!;
    const order = l.kind === "detail" ? FIELDS : (["name", "note"] as const);
    const k = (order as readonly FieldKey[]).indexOf(f);
    if (k >= 0 && k < order.length - 1) return focusField(lineId, order[k + 1]!);
    const nx = lines[i + 1];
    if (nx) return focusField(nx.id, "name");
    const add = blank();
    setRows([...lines, add], null, false);
    focusField(add.id, "name");
  };
  /** 1行を直す。数量・単価を変えたら金額は 数量×単価（既定。金額は打ち直せる） */
  const editLine = (lineId: string, x: Partial<PurchaseLine>, key: string) =>
    setRows(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        const n = { ...l, ...x };
        if (("quantity" in x || "unitPrice" in x) && !("amount" in x)) {
          const a = purchaseLineAmount(n.quantity, n.unitPrice);
          if (a != null) n.amount = a;
        }
        return n;
      }),
      `${lineId}:${key}`,
    );
  const lineMenu = (l: PurchaseLine, i: number): RowMenuItem[] => [
    { label: PURCHASE_MSG.lineAddBelow, testId: "purchase-line-add-below", onSelect: () => { const add = blank(); setRows([...lines.slice(0, i + 1), add, ...lines.slice(i + 1)], null, true); focusField(add.id, "name"); } },
    { label: PURCHASE_MSG.lineUp, testId: "purchase-line-up", disabled: i === 0, onSelect: () => { const n = [...lines]; [n[i - 1], n[i]] = [n[i]!, n[i - 1]!]; setRows(n, null, true); } },
    { label: PURCHASE_MSG.lineDown, testId: "purchase-line-down", disabled: i === lines.length - 1, onSelect: () => { const n = [...lines]; [n[i + 1], n[i]] = [n[i]!, n[i + 1]!]; setRows(n, null, true); } },
    { label: PURCHASE_MSG.lineDelete, danger: true, divider: true, testId: "purchase-line-delete", onSelect: () => setRows(lines.filter((x) => x.id !== l.id), null, true) },
  ];
  const keyNext = (lineId: string, f: FieldKey) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing && !e.shiftKey) {
      e.preventDefault();
      nextField(lineId, f);
    }
  };
  const origin = (l: PurchaseLine) =>
    l.orderLine ? (
      <span
        className="rqe-origin pce-origin"
        title={`${t("{order}")} ${p.order?.displayNo ?? ""}　${l.orderLine.quantity == null ? "" : `${fq(l.orderLine.quantity)} ${l.orderLine.unit} × ${yen(l.orderLine.unitPrice ?? 0)} ＝ `}${yen(l.orderLine.amount)}`}
        data-testid="purchase-origin"
      >
        {t(PURCHASE_MSG.originOrder(l.orderLine.no))}
      </span>
    ) : (
      <span className="chip rqe-own" data-testid="purchase-origin">{PURCHASE_MSG.originOwn}</span>
    );
  const rowsNode = lines.map((l, i) => {
    const on = (f: FieldKey) => focus?.id === l.id && focus.f === f;
    const inp = (f: FieldKey, node: ReactNode) => <span className={"rqe-cell" + (on(f) ? " is-on" : "")}>{node}</span>;
    const fprops = (f: FieldKey) => ({ "data-f": f, onFocus: () => setFocus({ id: l.id, f }), onBlur: () => undo.current.breakTyping() });
    const more = <td className="rqe-more">{draft ? <RowMenu label={PURCHASE_MSG.lineMenu.replace("{name}", l.name || "この行")} items={lineMenu(l, i)} testId="purchase-line-more" trigger={DOTS_H} triggerClass="rowmore dots" /> : null}</td>;
    if (l.kind !== "detail") {
      return (
        <tr key={l.id} className="line group" data-line={l.id} data-testid="purchase-line">
          <td className="no stk"><span className="nonum num">{i + 1}</span></td>
          <td className="name stk" colSpan={taxCol ? 7 : 6}>
            {draft ? inp("name", <input className="ce" value={l.name} maxLength={PURCHASE_LIMITS.name} aria-label={PURCHASE_MSG.headingPlaceholder} onChange={(e) => editLine(l.id, { name: e.target.value }, "name")} onKeyDown={keyNext(l.id, "name")} {...fprops("name")} data-testid="purchase-line-name" />) : <span className="nmt">{l.name}</span>}
          </td>
          <td className="note">{draft ? inp("note", <input className="ce" value={l.note} maxLength={PURCHASE_LIMITS.note} aria-label={t("{lineNote}")} onChange={(e) => editLine(l.id, { note: e.target.value }, "note")} onKeyDown={keyNext(l.id, "note")} {...fprops("note")} />) : <span className="cut">{l.note}</span>}</td>
          <td className="origin" />
          {more}
        </tr>
      );
    }
    return (
      <tr key={l.id} className="line detail" data-line={l.id} data-testid="purchase-line">
        <td className="no stk"><span className="nonum num">{i + 1}</span></td>
        <td className="name stk">{draft ? inp("name", <input className="ce" value={l.name} maxLength={PURCHASE_LIMITS.name} aria-label={t("{lineName}")} onChange={(e) => editLine(l.id, { name: e.target.value }, "name")} onKeyDown={keyNext(l.id, "name")} {...fprops("name")} data-testid="purchase-line-name" />) : <span className="nmt">{l.name}</span>}</td>
        <td className="spec">{draft ? inp("spec", <input className="ce" value={l.spec} maxLength={PURCHASE_LIMITS.spec} aria-label={t("{lineSpec}")} onChange={(e) => editLine(l.id, { spec: e.target.value }, "spec")} onKeyDown={keyNext(l.id, "spec")} {...fprops("spec")} />) : <span className="cut">{l.spec}</span>}</td>
        <td className="qty r">{draft ? inp("qty", <NumInput kind="dec" className="ce r" value={l.quantity} aria-label="数量" onChange={(v) => editLine(l.id, { quantity: v }, "qty")} onKeyDown={keyNext(l.id, "qty")} {...fprops("qty")} data-testid="purchase-line-qty" />) : <span className="num">{l.quantity == null ? "" : fq(l.quantity)}</span>}</td>
        <td className="unit">{draft ? inp("unit", <UnitInput id={`pu_${l.id}`} className="ce" value={l.unit} label="単位" onChange={(v) => editLine(l.id, { unit: v }, "unit")} onKeyDown={keyNext(l.id, "unit")} testId="purchase-line-unit" />) : l.unit}</td>
        <td className="price r">{draft ? inp("price", <NumInput kind="int" className="ce r" value={l.unitPrice} aria-label="単価" onChange={(v) => editLine(l.id, { unitPrice: v }, "price")} onKeyDown={keyNext(l.id, "price")} {...fprops("price")} data-testid="purchase-line-price" />) : <span className="num">{l.unitPrice == null ? "" : yen(l.unitPrice)}</span>}</td>
        <td className="amount r">{draft ? inp("amount", <NumInput kind="int" className="ce r" showZero value={l.amount} aria-label="金額" onChange={(v) => editLine(l.id, { amount: v ?? 0 }, "amount")} onKeyDown={keyNext(l.id, "amount")} {...fprops("amount")} data-testid="purchase-line-amount" />) : <span className="num">{yen(l.amount)}</span>}</td>
        {taxCol ? <td className="tax">{draft ? <TaxSelect id={`ptax_${l.id}`} value={l.taxCategory} onChange={(c) => editLine(l.id, { taxCategory: c }, "tax")} /> : <span title={TAX_CATEGORY_LABEL[l.taxCategory]}>{TAX_CATEGORY_LABEL[l.taxCategory]}</span>}</td> : null}
        <td className="note">{draft ? inp("note", <input className="ce" value={l.note} maxLength={PURCHASE_LIMITS.note} aria-label={t("{lineNote}")} onChange={(e) => editLine(l.id, { note: e.target.value }, "note")} onKeyDown={keyNext(l.id, "note")} {...fprops("note")} />) : <span className="cut">{l.note}</span>}</td>
        <td className="origin">{origin(l)}</td>
        {more}
      </tr>
    );
  });

  const vendorOpts = [{ id: p.vendor.id, name: p.vendor.displayName }, ...(vendors.data?.items ?? []).filter((v) => v.id !== p.vendor.id).map((v) => ({ id: v.id, name: v.displayName }))];
  const groupOpts = [...(p.group ? [{ id: p.group.id, name: p.group.name }] : []), ...(groups.data?.items ?? []).filter((g) => g.id !== p.group?.id).map((g) => ({ id: g.id, name: g.name }))];
  const credit = creditRatioText(p.creditRatio, p.creditUntil);

  return (
    <div className={"rqe pce" + (narrow ? " is-phone" : "")} data-testid="purchase-page">
      {/* #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝仕入の絵と名前・{vendor}（題）・状態｜合計（税込）。3段目＝請求日・請求書番号・{project}・元・原本｜詳細 */}
      <DocHead
        back={back}
        save={
          <span className="autosave dh-save2 vql-saved rqe-saved pce-saved" aria-live="polite" data-testid="purchase-saved">
            <span>{sv.state.kind === "saving" ? PURCHASE_MSG.saving : sv.state.kind === "error" ? `⚠ ${sv.state.msg}` : "自動保存"}</span>{" "}
            <span className="num">{sv.state.kind === "saved" ? sv.state.at : "\u00a0"}</span>
          </span>
        }
        undo={
          draft ? (
            <span className="undo rqe-undo" role="group" aria-label="元に戻す・やり直す">
              <button type="button" className="undobtn rqe-ubtn" aria-label="元に戻す" title="元に戻す" disabled={!undo.current.canUndo} onClick={() => undoDo(false)} data-testid="purchase-undo">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></svg>
              </button>
              <button type="button" className="undobtn rqe-ubtn" aria-label="やり直す" title="やり直す" disabled={!undo.current.canRedo} onClick={() => undoDo(true)} data-testid="purchase-redo">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" /></svg>
              </button>
            </span>
          ) : null
        }
        primary={actions.length ? <span className="hd-acts" data-testid="head-actions"><HeadActions actions={actions} label={PURCHASE_MSG.title} primary part="main" /></span> : null}
        menu={actions.length ? <HeadActions actions={actions} label={PURCHASE_MSG.title} part="more" /> : null}
        kind="purchases"
        kindLabel={PURCHASE_MSG.title}
        title={
          draft ? (
            <label className="vql-fld rqe-f pce-vendor dh-vsel">
              <span className="sr">{L.vendor}</span>
              <select value={p.vendor.id} aria-label={L.vendor} onChange={(e) => patch({ vendorId: e.target.value }, true)} data-testid="purchase-vendor">
                {vendorOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          ) : (
            <b className="pce-vname dh-vname" title={p.vendor.name} data-testid="purchase-vendor-text">{p.vendor.displayName}</b>
          )
        }
        chips={<PurchaseStateChip s={p.payState} dueOn={p.paymentDueOn} overdueDays={p.overdueDays} />}
        total={{ total, subtotal: draft ? subtotal : p.subtotal, taxTotal: total - (draft ? subtotal : p.subtotal) }}
        meta={
          <>
            <DocField label={PURCHASE_MSG.billDate}>
              {draft ? <DateInput inline value={head.purchasedOn} label={PURCHASE_MSG.billDate} ariaLabel={PURCHASE_MSG.billDate} clearable={false} onChange={(v) => v && setH({ purchasedOn: v }, true)} testId="purchase-date" /> : <b className="num">{mdSlash(head.purchasedOn)}</b>}
            </DocField>
            <DocField label={<label htmlFor="pu_no">{PURCHASE_MSG.invoiceNo}</label>}>
              {draft ? <input id="pu_no" className="inp pce-no" value={head.vendorInvoiceNo} maxLength={PURCHASE_LIMITS.invoiceNo} onChange={(e) => setH({ vendorInvoiceNo: e.target.value })} data-testid="purchase-invoice-no" /> : <b className="num" data-testid="purchase-invoice-no-text">{head.vendorInvoiceNo || "—"}</b>}
            </DocField>
            <DocField label={L.project}>
              {p.group ? <Link to={`/groups/${p.group.id}`} data-testid="purchase-group-link">{p.group.name} ›</Link> : <span className="muted">—</span>}
            </DocField>
            {p.order ? (
              <DocField label={PURCHASE_MSG.origin}>
                <Link to={`/orders/${p.order.id}/paper`} data-testid="purchase-order-link">{`${t("{order}")} ${p.order.displayNo ?? ""}`} ›</Link>
              </DocField>
            ) : null}
            {/* 元の支払通知書（#233 3b）：確認済で起こした仕入の原本＝その支払通知書の紙 */}
            {p.paymentNotice && !p.received ? (
              <DocField label={PURCHASE_MSG.original}>
                <Link to={`/payment-notices/${p.paymentNotice.id}/paper`} data-testid="purchase-original-notice">{`${p.paymentNotice.name} ${p.paymentNotice.displayNo ?? ""}`.trim()} ›</Link>
              </DocField>
            ) : null}
            {p.received ? (
              <DocField label={PURCHASE_MSG.original}>
                {p.received.url ? <a href={p.received.url} target="_blank" rel="noopener" title={p.received.title} data-testid="purchase-original-link">{p.received.title} ›</a> : <span>{p.received.title}</span>}
              </DocField>
            ) : null}
          </>
        }
        tools={
          <button type="button" className="btn sm quiet detailbtn" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="purchase-details">
            {PURCHASE_MSG.detail} <span className={"vqd-chev" + (open ? " is-open" : "")} aria-hidden="true" />
          </button>
        }
        testId="purchase-head"
      >
        {open ? (
          <div className="vqd-r3" data-testid="purchase-details-body">
            <Field id="pu_memo" label={PURCHASE_MSG.memo} style={{ width: "100%" }}>
              <textarea id="pu_memo" className="inp" rows={2} value={head.memo} maxLength={PURCHASE_LIMITS.memo} readOnly={!live} onChange={(e) => setH({ memo: e.target.value })} data-testid="purchase-memo" />
            </Field>
          </div>
        ) : null}
      </DocHead>

      {/* 条件の帯 */}
      <section className="rqe-band pce-terms" aria-label={PURCHASE_MSG.terms} data-testid="purchase-terms">
        <b className="pce-bt">{PURCHASE_MSG.terms}</b>
        <div className="pce-tf">
          <span>{PURCHASE_MSG.receivedOn}</span>
          {draft ? <DateInput value={head.receivedOn ?? ""} label={PURCHASE_MSG.receivedOn} ariaLabel={PURCHASE_MSG.receivedOn} onChange={(v) => setH({ receivedOn: v || null }, true)} testId="purchase-received-on" /> : <b className="num">{head.receivedOn ? mdSlash(head.receivedOn) : "—"}</b>}
        </div>
        <div className="pce-tf">
          <span>{PURCHASE_MSG.inspectedOn}</span>
          {draft ? <DateInput value={head.inspectedOn ?? ""} label={PURCHASE_MSG.inspectedOn} ariaLabel={PURCHASE_MSG.inspectedOn} onChange={(v) => setH({ inspectedOn: v || null }, true)} testId="purchase-inspected-on" /> : <b className="num">{head.inspectedOn ? mdSlash(head.inspectedOn) : "—"}</b>}
        </div>
        <div className={"pce-tf" + (over60 ? " is-over" : "")}>
          <span>
            {PURCHASE_MSG.dueOn}
            <Help label={`${PURCHASE_MSG.dueOn}の説明`} text={t(PURCHASE_MSG.helpDue)} testId="help-purchase-due" />
          </span>
          {draft ? <DateInput value={head.paymentDueOn ?? ""} label={PURCHASE_MSG.dueOn} ariaLabel={PURCHASE_MSG.dueOn} onChange={(v) => setH({ paymentDueOn: v || null }, true)} testId="purchase-due-on" /> : <b className={"num" + (over60 ? " shu" : "")}>{head.paymentDueOn ? mdSlash(head.paymentDueOn) : "—"}</b>}
          {over60 ? <span className="pill shu pce-60" data-testid="purchase-over60">{PURCHASE_MSG.over60(over60)}</span> : null}
        </div>
        <div className="pce-tinfo">
        <div className="pce-tf pce-kv2">
          <span>{PURCHASE_MSG.registration}</span>
          <b className="num" title={p.vendor.registrationNo ? undefined : PURCHASE_MSG.registrationNone} data-testid="purchase-registration">{p.vendor.registrationNo ?? PURCHASE_MSG.registrationNoneShort}</b>
        </div>
        <div className="pce-tf pce-kv2">
          <span>
            {PURCHASE_MSG.credit}
            <Help label={`${PURCHASE_MSG.credit}の説明`} text={t(PURCHASE_MSG.helpCredit)} testId="help-purchase-credit" />
          </span>
          <b data-testid="purchase-credit">{credit ?? PURCHASE_MSG.creditFull}</b>
        </div>
        </div>
      </section>

      {/* 明細のカード */}
      <section className="rqe-main" aria-label="明細">
        <div className="rqe-tools" data-testid="purchase-tools">
          {draft ? (
            <>
              <button type="button" className="btn" onClick={() => { const add = blank(); setRows([...lines, add], null, true); focusField(add.id, "name"); }} data-testid="purchase-add-line">{PURCHASE_MSG.addLine}</button>
              <button type="button" className="btn text" onClick={() => { const add = blank("heading"); setRows([...lines, add], null, true); focusField(add.id, "name"); }} data-testid="purchase-add-heading">{PURCHASE_MSG.addHeading}</button>
            </>
          ) : null}
          <span className="sp" />
          <span className="muted num rqe-small" data-testid="purchase-line-summary">{t(PURCHASE_MSG.linesFrom(lines.length, p.order?.displayNo ?? null))}</span>
        </div>
        <EstimateTable ctx={ctx} customer={L.customer} foot={null} testId="purchase-table" sizer={narrow ? undefined : sizer}>
          {rowsNode}
        </EstimateTable>
        <div className="pce-sums" data-testid="purchase-sums">
          <div className="pce-sum">
            <span>{PURCHASE_MSG.subtotal}</span>
            <b className="num" data-testid="purchase-subtotal">{yen(draft ? subtotal : p.subtotal)}</b>
            <span className="u">円</span>
          </div>
          <div className="pce-sum">
            <span>
              {p.taxRate != null ? PURCHASE_MSG.taxRate(Math.round(p.taxRate * 1000) / 10) : PURCHASE_MSG.tax}
              <Help label={`${PURCHASE_MSG.tax}の説明`} text={PURCHASE_MSG.helpTax} testId="help-purchase-tax" />
            </span>
            {draft ? (
              <NumInput
                kind="int"
                className="inp r pce-tax"
                showZero
                value={taxValue}
                aria-label={PURCHASE_MSG.tax}
                onChange={(v) => {
                  setTaxText(v ?? 0);
                  patch({ taxAmount: v ?? 0 });
                }}
                data-testid="purchase-tax"
              />
            ) : (
              <b className="num" data-testid="purchase-tax-text">{yen(p.taxAmount)}</b>
            )}
            <span className="u">円</span>
            {draft && !taxAuto ? (
              <button type="button" className="btn text pce-taxauto" onClick={() => { setTaxText(null); patch({ taxAmount: null }, true); }} data-testid="purchase-tax-auto">
                {PURCHASE_MSG.taxAutoBack}
              </button>
            ) : null}
          </div>
          <div className="pce-sum is-total">
            <span>{PURCHASE_MSG.total}</span>
            <b className="num" data-testid="purchase-total">{yen(total)}</b>
            <span className="u">円</span>
          </div>
        </div>
      </section>

      {/* 下の帯：原価計上・付け先・原価・{order}との差 */}
      <section className="rqe-band pce-cost" data-testid="purchase-cost">
        <span className="pce-sw">
          <Switch checked={p.costPosting} disabled={!live} onChange={(v) => { qc.setQueryData<Purchase>(KEY(p.id), (o) => (o ? { ...o, costPosting: v } : o)); patch({ costPosting: v }, true); }} label={PURCHASE_MSG.costPosting} testId="purchase-cost-posting" />
          <Help label={`${PURCHASE_MSG.costPosting}の説明`} text={t(PURCHASE_MSG.helpCostPosting)} testId="help-purchase-cost" />
        </span>
        <label className="vql-fld pce-target">
          <span>{L.project}</span>
          {live ? (
            <select
              value={p.group?.id ?? ""}
              onChange={(e) => {
                const g = groupOpts.find((x) => x.id === e.target.value) ?? null;
                qc.setQueryData<Purchase>(KEY(p.id), (o) => (o ? { ...o, group: g } : o));
                patch({ projectGroupId: g?.id ?? null }, true);
              }}
              data-testid="purchase-group"
            >
              <option value="">{t(PURCHASE_MSG.noProject)}</option>
              {groupOpts.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          ) : (
            <b>{p.group?.name ?? "—"}</b>
          )}
        </label>
        <span className="pce-kv">
          <span>{PURCHASE_MSG.cost}</span>
          <b className="num" data-testid="purchase-cost-amount">{p.costPosting && p.group ? yen(draft ? subtotal : p.subtotal) : "—"}</b>
          {p.costPosting && p.group ? <span className="u">円</span> : null}
        </span>
        <span className="sp" />
        {orderDiff != null ? (
          <span className="pce-kv" data-testid="purchase-order-diff">
            <span>{t(PURCHASE_MSG.orderDiff)}</span>
            <b className="num">{orderDiff > 0 ? "+" : orderDiff < 0 ? "−" : "±"}{yen(Math.abs(orderDiff))}</b>
            <span className="u">円</span>
          </span>
        ) : null}
      </section>

      {/* 節「支払」（#233 3c）：確定・取消の仕入。残・「支払登録」（PC）・履歴 */}
      {p.status !== "draft" ? <PurchasePaymentsSection purchaseId={p.id} vendorId={p.vendor.id} vendorName={p.vendor.displayName} confirmed={p.status === "confirmed"} /> : null}

      {back.dialog}
      {sv.dialog}
      {dlg === "confirm60" ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label={PURCHASE_MSG.confirmTitle} onKeyDown={(e) => e.key === "Escape" && setDlg(null)}>
            <div className="dh">{PURCHASE_MSG.confirmTitle}</div>
            <div className="db"><p style={{ margin: 0 }} className="shu">{PURCHASE_MSG.confirmOver60.replace("{n}", String(over60 ?? 0))}</p></div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void confirm(true)} data-testid="purchase-confirm-60">{PURCHASE_MSG.confirm}</button>
            </div>
          </div>
        </div>
      ) : null}
      {dlg === "void" ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label={PURCHASE_MSG.voidTitle} onKeyDown={(e) => e.key === "Escape" && setDlg(null)}>
            <div className="dh">{PURCHASE_MSG.voidTitle}</div>
            <div className="db">
              <p style={{ marginTop: 0 }}>{PURCHASE_MSG.voidConfirm}</p>
              <Field id="pu_reason" label="理由（任意）">
                <input id="pu_reason" className="inp" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-testid="purchase-void-reason" />
              </Field>
            </div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setDlg(null)}>閉じる</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void act(() => api<Purchase>("POST", `/purchases/${p.id}/void`, { reason, version: version.current }), PURCHASE_MSG.voided)} data-testid="purchase-void-confirm">{PURCHASE_MSG.void}</button>
            </div>
          </div>
        </div>
      ) : null}
      {dlg === "delete" ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label="削除" onKeyDown={(e) => e.key === "Escape" && setDlg(null)}>
            <div className="dh">削除</div>
            <div className="db"><p style={{ margin: 0 }}>この仕入の下書きを削除しますか</p></div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void doDelete()} data-testid="purchase-delete-confirm">削除</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
